import { DISMISSAL_STORAGE_KEY, installOfferFor, parseDismissedAt } from '../install.js';
import type { InstallOffer } from '../install.js';
import { t } from '../i18n/index.js';
import { el } from './dom.js';

/** Chromium's non-standard event carrying the deferred install prompt. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const readDismissedAt = (): number | null => parseDismissedAt(window.localStorage.getItem(DISMISSAL_STORAGE_KEY));

const rememberDismissal = (): void => window.localStorage.setItem(DISMISSAL_STORAGE_KEY, String(Date.now()));

const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

type Banner = Readonly<{ element: HTMLElement; remove: () => void }>;

const createBanner = (text: string, action: HTMLElement | null): Banner => {
  const close = el('button', { className: 'install__close', type: 'button', ariaLabel: t('action.dismiss'), text: '×' });
  const body = el('div', { className: 'install__body' }, [
    el('span', { className: 'install__title', text: t('install.title') }),
    el('span', { className: 'install__text', text }),
  ]);
  const children: HTMLElement[] = action === null ? [body, close] : [body, action, close];
  const element = el('div', { className: 'install' }, [el('span', { className: 'install__icon' }), ...children]);
  const remove = (): void => {
    element.classList.add('install--leaving');
    element.addEventListener('transitionend', () => element.remove(), { once: true });
  };
  close.addEventListener('click', () => {
    rememberDismissal();
    remove();
  });
  return { element, remove };
};

const showIosInstructions = (root: HTMLElement): void => {
  const banner = createBanner(t('install.ios'), null);
  root.prepend(banner.element);
};

const waitForNativePrompt = (root: HTMLElement): void => {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    const prompt = event as BeforeInstallPromptEvent;
    const button = el('button', { className: 'button install__button', type: 'button', text: t('install.button') });
    const banner = createBanner(t('install.native'), button);
    button.addEventListener('click', () => {
      prompt
        .prompt()
        .then(() => prompt.userChoice)
        .then((choice) => {
          if (choice.outcome === 'dismissed') {
            rememberDismissal();
          }
          banner.remove();
        })
        .catch((error: unknown) => {
          throw new Error(`Install prompt failed: ${String(error)}`);
        });
    });
    root.prepend(banner.element);
  });
  window.addEventListener('appinstalled', () => {
    document.querySelector('.install')?.remove();
  });
};

/** Decides once per page load whether and how to offer installation. */
export const setupInstallBanner = (root: HTMLElement): InstallOffer => {
  const offer = installOfferFor({
    standalone: isStandalone(),
    userAgent: navigator.userAgent,
    dismissedAtMs: readDismissedAt(),
    nowMs: Date.now(),
  });
  switch (offer) {
    case 'ios-instructions':
      showIosInstructions(root);
      break;
    case 'native-prompt':
      waitForNativePrompt(root);
      break;
    case 'none':
      break;
  }
  return offer;
};
